// GPSIndicator.js - Real-time GPS status display for check-in modal
import React, { useState, useEffect } from 'react';
import { MapPin, AlertCircle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function GPSIndicator({
  targetLat,
  targetLon,
  geoFenceRadius = 150,
  gymName,
  onLocationUpdate,
  onRetry,
  showRetryButton = false,
  className = ''
}) {
  const [location, setLocation] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [distance, setDistance] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, good, warning, error
  const [errorMessage, setErrorMessage] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Haversine formula for distance calculation
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus('error');
      setErrorMessage('GPS not supported on this device');
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy: acc } = position.coords;
        setLocation({ lat: latitude, lon: longitude });
        setAccuracy(acc);

        // Calculate distance if target provided
        if (targetLat && targetLon) {
          const dist = calculateDistance(latitude, longitude, targetLat, targetLon);
          setDistance(dist);

          // Determine status based on distance and accuracy
          if (dist <= geoFenceRadius && acc < 100) {
            setStatus('good');
          } else if (dist > geoFenceRadius) {
            setStatus('warning');
          } else {
            // Within fence but poor accuracy
            setStatus(acc < 100 ? 'good' : 'warning');
          }
        } else {
          // No target, just show accuracy status
          setStatus(acc < 50 ? 'good' : acc < 100 ? 'warning' : 'error');
        }

        setErrorMessage(null);
        onLocationUpdate?.({ latitude, longitude, accuracy: acc });
      },
      (error) => {
        setStatus('error');
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setErrorMessage('Location permission denied. Enable it in browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            setErrorMessage('Location unavailable. Try moving outdoors.');
            break;
          case error.TIMEOUT:
            setErrorMessage('Location request timed out. Retrying...');
            break;
          default:
            setErrorMessage('Unable to get location');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [targetLat, targetLon, geoFenceRadius, onLocationUpdate, retryCount]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setStatus('loading');
    setErrorMessage(null);
    onRetry?.();
  };

  const formatDistance = (meters) => {
    if (meters < 1000) return `${Math.round(meters)}m`;
    return `${(meters / 1000).toFixed(1)}km`;
  };

  // --- State 1: Acquiring GPS ---
  if (status === 'loading') {
    return (
      <div className={`rounded-xl px-4 py-3 bg-slate-50 border border-slate-200 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <MapPin className="w-5 h-5 text-slate-400 animate-pulse" />
            <Loader2 className="w-3 h-3 text-slate-400 animate-spin absolute -bottom-0.5 -right-0.5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-600">Getting your location...</p>
            {accuracy && (
              <p className="text-xs text-slate-400 mt-0.5">Accuracy: {Math.round(accuracy)}m</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- State 2: GPS Error ---
  if (status === 'error') {
    return (
      <div className={`rounded-xl px-4 py-3 bg-red-50 border border-red-200 ${className}`}>
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-700">{errorMessage || 'Location error'}</p>
          </div>
          {showRetryButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              className="flex-shrink-0 text-red-600 border-red-200 hover:bg-red-100 h-8 px-3"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  // --- State 3: GPS Acquired, within geofence ---
  if (status === 'good') {
    return (
      <div className={`rounded-xl px-4 py-3 bg-emerald-50 border border-emerald-200 ${className}`}>
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-700">Location confirmed</p>
            {distance !== null && (
              <p className="text-xs text-emerald-600/70 mt-0.5">
                You are {formatDistance(distance)} from {gymName || 'the gym'}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- State 4: GPS Acquired, outside geofence or poor accuracy ---
  return (
    <div className={`rounded-xl px-4 py-3 bg-amber-50 border border-amber-200 ${className}`}>
      <div className="flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-700">
            {distance !== null && distance > geoFenceRadius
              ? 'You are outside the gym zone'
              : 'GPS signal is weak'}
          </p>
          <p className="text-xs text-amber-600/70 mt-0.5">
            {distance !== null && distance > geoFenceRadius
              ? `${formatDistance(distance)} away (max ${geoFenceRadius}m). Move closer to the gym entrance.`
              : `Accuracy: ${Math.round(accuracy || 0)}m. Step outside or near a window.`}
          </p>
        </div>
        {showRetryButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRetry}
            className="flex-shrink-0 text-amber-600 border-amber-200 hover:bg-amber-100 h-8 px-3"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}

export default GPSIndicator;
